import React, { useState } from 'react';
import { RotateCcw, CheckCircle2, Circle } from 'lucide-react';

const PreopChecklist = ({ onClose }) => {
  const [checkedItems, setCheckedItems] = useState({});

  const toggleItem = (itemId) => {
    setCheckedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const resetChecklist = () => {
    setCheckedItems({});
  };

  const checklistItems = [
    {
      id: 'preop-note',
      text: 'Anesthesia pre-op note filled out',
      category: 'documentation'
    },
    {
      id: 'consent',
      text: 'Anesthesia consent done',
      boldText: 'and witnessed by an RN',
      category: 'documentation'
    },
    {
      id: 'type-screen',
      text: 'Active Type and Screen (if necessary)',
      category: 'labs'
    },
    {
      id: 'npo',
      text: 'Appropriate NPO status',
      category: 'orders'
    },
    {
      id: 'pregnancy',
      text: 'Pregnancy test (Any female 55 or younger, unless h/o hysterectomy)\nNote: tubal ligation is NOT an exclusion',
      category: 'labs'
    },
    {
      id: 'plavix',
      text: 'On Plavix, need for pre-op platelet function test?',
      category: 'labs'
    }
  ];

  const abnormalLabsItems = [
    { id: 'wbc', text: 'White count' },
    { id: 'inr', text: 'INR' },
    { id: 'potassium', text: 'Potassium' },
    { id: 'glucose', text: 'Blood sugar' }
  ];

  // Calculate completion
  const getActualTotal = () => {
    return checklistItems.length + 1; // base items + abnormal labs question
  };

  const getCompletedCount = () => {
    let count = 0;
    // Count main checklist items
    checklistItems.forEach(item => {
      if (checkedItems[item.id]) count++;
    });
    // Count abnormal labs question
    if (checkedItems['abnormal-labs'] !== undefined) count++;
    return count;
  };

  const actualTotal = getActualTotal();
  const actualCompleted = getCompletedCount();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl flex flex-col space-y-4">
      {/* Header Bar */}
      <div className="flex items-center justify-between p-6 pb-0">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">For Next Day's Inpatients</h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={resetChecklist}
            className="text-sm text-[#9F8D70] hover:text-[#8a7a61] font-medium flex items-center gap-1"
          >
            <RotateCcw size={14} />
            Reset
          </button>
        </div>
      </div>

      {/* Checklist Content */}
      <div className="flex-1 overflow-y-auto p-6 pt-3">
          <div className="space-y-3">
            {checklistItems.map((item) => {
              const textParts = item.text.split('\n');
              const mainText = textParts[0];
              const noteText = textParts[1];

              return (
                <button
                  key={item.id}
                  onClick={() => toggleItem(item.id)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    checkedItems[item.id]
                      ? 'bg-green-50 border-green-400'
                      : 'bg-white border-gray-200 hover:border-blue-500 dark:hover:border-amber-400'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {checkedItems[item.id] ? (
                        <CheckCircle2 size={20} className="text-green-600" />
                      ) : (
                        <Circle size={20} className="text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm ${
                        checkedItems[item.id] ? 'text-green-900 line-through' : 'text-gray-800'
                      }`}>
                        {mainText}
                        {item.boldText && (
                          <> <strong>{item.boldText}</strong></>
                        )}
                      </p>
                      {noteText && (
                        <p className="text-xs text-gray-600 italic mt-1">
                          <strong>Note:</strong> {noteText.replace('Note: ', '')}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}

            {/* Abnormal Labs Section */}
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
              <button
                onClick={() => toggleItem('abnormal-labs')}
                className="w-full text-left flex items-start gap-3"
              >
                <div className="flex-shrink-0 mt-0.5">
                  {checkedItems['abnormal-labs'] !== undefined ? (
                    checkedItems['abnormal-labs'] ? (
                      <CheckCircle2 size={20} className="text-amber-600" />
                    ) : (
                      <CheckCircle2 size={20} className="text-green-600" />
                    )
                  ) : (
                    <Circle size={20} className="text-gray-400" />
                  )}
                </div>
                <div className="flex-1">
                  <p className={`font-semibold text-sm ${
                    checkedItems['abnormal-labs'] !== undefined
                      ? checkedItems['abnormal-labs']
                        ? 'text-amber-900'
                        : 'text-green-900 line-through'
                      : 'text-amber-900'
                  }`}>
                    Grossly abnormal labs that need to be addressed?
                  </p>
                </div>
              </button>

              {checkedItems['abnormal-labs'] && (
                <div className="mt-4 ml-8">
                  <p className="text-sm font-medium text-gray-800 mb-2">
                    Review and address abnormal values:
                  </p>
                  <ul className="space-y-1.5 ml-4">
                    {abnormalLabsItems.map((lab) => (
                      <li key={lab.id} className="text-sm text-gray-700 flex items-start gap-2">
                        <span className="text-amber-600 flex-shrink-0">•</span>
                        <span>{lab.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Completion Message */}
          {actualCompleted === actualTotal && actualTotal > 0 && (
            <div className="mt-6 bg-green-100 dark:bg-green-900/20 border-2 border-green-400 dark:border-green-600 rounded-lg p-4 text-center">
              <CheckCircle2 size={32} className="text-green-600 dark:text-green-400 mx-auto mb-2" />
              <p className="font-bold text-green-900 dark:text-green-100">Checklist Complete!</p>
              <p className="text-sm text-green-800 dark:text-green-200 mt-1">All preop items have been addressed.</p>
            </div>
          )}
        </div>
    </div>
  );
};

export default PreopChecklist;
